import { useEffect, useState } from "react"
import { motion, AnimatePresence } from "motion/react"
import { ArrowRight, Check, Loader2, AlertTriangle } from "lucide-react"
import { Navigate } from "react-router-dom"
import { useSession } from "@/hooks/use-session"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"

type Tier = "pulse" | "stream" | "surge"
type Interval = "monthly" | "annual"

type Plan = {
  tier?: Tier
  name: string
  tagline: string
  monthlyPrice?: string
  annualPrice?: string
  customPrice?: string
  customCaption?: string
  customCaption2?: string
  highlight?: boolean
  enterprise?: boolean
  features: string[]
}

// Mirrors the marketing pricing page (website/src/components/pricing.tsx).
// Keep the tiers, prices, and feature lists in sync with that file.
const PLANS: Plan[] = [
  {
    tier: "pulse",
    name: "Pulse",
    tagline: "For indie devs and small teams shipping real webhooks.",
    monthlyPrice: "$29",
    annualPrice: "$24",
    features: [
      "250K events / month",
      "7-day event retention",
      "3 team seats",
      "All 37 providers verified out of the box",
      "Inbound and outbound webhooks",
      "OAuth2 and mTLS destinations",
      "9 SDKs and Terraform provider",
      "Email support",
    ],
  },
  {
    tier: "stream",
    name: "Stream",
    tagline: "For growing companies running production webhook traffic.",
    monthlyPrice: "$149",
    annualPrice: "$124",
    highlight: true,
    features: [
      "5M events / month",
      "30-day event retention",
      "Unlimited team seats",
      "Everything in Pulse, plus:",
      "JavaScript transformations",
      "Local-dev tunnel",
      "Email and Slack alerting",
      "Portal links for your customers",
      "Datadog and New Relic metrics export",
      "OpenTelemetry OTLP tracing",
      "90-day audit log retention",
      "Priority email support",
    ],
  },
  {
    tier: "surge",
    name: "Surge",
    tagline: "For high-volume teams that need more headroom and a tighter SLA.",
    monthlyPrice: "$349",
    annualPrice: "$299",
    features: [
      "15M events / month",
      "90-day event retention",
      "1-year audit log retention",
      "Unlimited team seats",
      "Everything in Stream, plus:",
      "SAML and OIDC single sign-on",
      "Multi-org and sub-organizations",
      "SQS, Kafka, PubSub, EventBridge, RabbitMQ sinks",
      "Long-term sinks to S3 and Postgres",
      "Static egress IP for firewall allowlisting",
      "99.99% uptime SLA",
      "Priority support with faster response",
    ],
  },
  {
    name: "Torrent",
    tagline: "For teams with custom volume, compliance, or support needs.",
    customPrice: "Custom",
    customCaption: "Tailored to your scale",
    customCaption2: "Annual contract, volume pricing",
    enterprise: true,
    features: [
      "Custom event volume and retention",
      "Configurable audit log retention",
      "Everything in Surge, plus:",
      "SCIM provisioning",
      "IP allowlisting",
      "SOC 2 report on request",
      "Self-hosted distribution",
      "Custom SLA",
      "Dedicated Slack channel and named contact",
      "Onboarding session with engineering",
      "Quarterly business reviews",
      "Vendor security review support",
      "Signed contract, DPA, Net-30 invoice billing",
    ],
  },
]

const TIER_RANK: Record<string, number> = { free: 0, pulse: 1, stream: 2, surge: 3, torrent: 4 }

export function UpgradePage() {
  const session = useSession()
  const [interval, setBillingInterval] = useState<Interval>("annual")
  const [loadingTier, setLoadingTier] = useState<Tier | null>(null)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [confirmDowngrade, setConfirmDowngrade] = useState<Tier | null>(null)

  useEffect(() => {
    const reset = () => {
      setLoadingTier(null)
      setErrorMsg(null)
    }
    window.addEventListener("pageshow", reset)
    return () => window.removeEventListener("pageshow", reset)
  }, [])

  if (session.status === "loading") return null
  if (session.status !== "authenticated") return <Navigate to="/login" replace />

  const requestCheckout = (tier: Tier) => {
    const currentTier = session.user.tier
    const onPaid =
      session.user.billing_status === "active" ||
      session.user.billing_status === "past_due" ||
      session.user.billing_status === "cancelled"
    const fromRank = currentTier ? TIER_RANK[currentTier] : undefined
    const toRank = TIER_RANK[tier]
    if (onPaid && fromRank !== undefined && toRank !== undefined && toRank < fromRank) {
      setConfirmDowngrade(tier)
      return
    }
    void startCheckout(tier)
  }

  const startCheckout = async (tier: Tier) => {
    setLoadingTier(tier)
    setErrorMsg(null)
    setConfirmDowngrade(null)
    try {
      const res = await fetch("/api/v1/saas/billing/checkout", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tier, interval }),
      })
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string }
        setErrorMsg(body.error || "Couldn't start checkout. Try again in a moment.")
        setLoadingTier(null)
        window.scrollTo({ top: 0, behavior: "smooth" })
        return
      }
      const { url } = (await res.json()) as { url: string }
      window.location.assign(url)
    } catch {
      setErrorMsg("Couldn't reach the server. Try again in a moment.")
      setLoadingTier(null)
    }
  }

  return (
    <div className="mx-auto max-w-7xl py-8">
      <div className="text-center">
        <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">Pick a plan</h1>
        <p className="mx-auto mt-3 max-w-2xl text-sm text-muted-foreground">
          Every paid plan includes every feature. You only pay for volume and seats.
          Change or cancel anytime, and we never raise prices on existing customers.
        </p>
      </div>

      {errorMsg && (
        <div
          role="alert"
          className="mx-auto mt-6 flex max-w-2xl items-start gap-3 rounded-lg border border-rose-500/30 bg-rose-500/8 px-4 py-3 text-sm text-rose-500"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <div className="flex-1">{errorMsg}</div>
          <button
            type="button"
            onClick={() => setErrorMsg(null)}
            className="text-xs text-rose-400 hover:text-rose-300"
          >
            Dismiss
          </button>
        </div>
      )}

      <div className="mt-8 flex justify-center">
        <BillingToggle interval={interval} onChange={setBillingInterval} />
      </div>

      <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-4 lg:gap-4">
        {PLANS.map((plan, idx) => (
          <PlanCard
            key={plan.name}
            plan={plan}
            interval={interval}
            index={idx}
            loading={plan.tier ? loadingTier === plan.tier : false}
            currentTier={session.user.tier}
            currentInterval={session.user.billing_interval}
            billingStatus={session.user.billing_status}
            onCheckout={requestCheckout}
          />
        ))}
      </div>

      {confirmDowngrade && session.user.tier && (
        <DowngradeConfirm
          fromTier={session.user.tier}
          toTier={confirmDowngrade}
          onCancel={() => setConfirmDowngrade(null)}
          onConfirm={() => void startCheckout(confirmDowngrade)}
        />
      )}
    </div>
  )
}

const TIER_NAME: Record<string, string> = {
  free: "Free",
  pulse: "Pulse",
  stream: "Stream",
  surge: "Surge",
  torrent: "Torrent",
}

interface DowngradeImpact {
  events_from: number
  events_to: number
  retention_from: number
  retention_to: number
  streams_clamped: number
  queue_destinations_paused: number
  sinks_paused: number
  alerts_disabled: number
  transforms_disabled: number
  sso_disabled: boolean
  observability_warn: boolean
  portal_links_warn: number
  seats_over: number
}

type ImpactLine = { text: string; warn?: boolean }

function eventsLabel(n: number): string {
  return n < 0 ? "unlimited" : n.toLocaleString()
}

function impactLines(imp: DowngradeImpact, toName: string): ImpactLine[] {
  const lines: ImpactLine[] = []
  if (imp.events_to !== imp.events_from) {
    lines.push({
      text: `Monthly event limit drops to ${eventsLabel(imp.events_to)} (from ${eventsLabel(imp.events_from)}).`,
    })
  }
  if (imp.streams_clamped > 0) {
    lines.push({
      text: `${imp.streams_clamped} stream${imp.streams_clamped > 1 ? "s" : ""} will be clamped to ${imp.retention_to}-day retention; older entries get swept.`,
    })
  }
  if (imp.queue_destinations_paused > 0) {
    lines.push({
      text: `${imp.queue_destinations_paused} queue destination${imp.queue_destinations_paused > 1 ? "s" : ""} (Kafka/SQS/PubSub/etc.) will be paused.`,
    })
  }
  if (imp.sinks_paused > 0) {
    lines.push({
      text: `${imp.sinks_paused} long-term sink${imp.sinks_paused > 1 ? "s" : ""} (S3/Postgres) will be paused.`,
    })
  }
  if (imp.alerts_disabled > 0) {
    lines.push({
      text: `${imp.alerts_disabled} alert rule${imp.alerts_disabled > 1 ? "s" : ""} will be disabled.`,
    })
  }
  if (imp.transforms_disabled > 0) {
    lines.push({
      text: `${imp.transforms_disabled} transformation${imp.transforms_disabled > 1 ? "s" : ""} will be disabled.`,
    })
  }
  if (imp.sso_disabled) {
    lines.push({ text: "Single sign-on will be turned off (your config is kept for re-upgrade)." })
  }
  if (imp.observability_warn) {
    lines.push({ text: "Datadog, New Relic, and OTLP export will stop." })
  }
  if (imp.portal_links_warn > 0) {
    lines.push({
      text: `${imp.portal_links_warn} active portal link${imp.portal_links_warn > 1 ? "s" : ""} stay live for now — review them before downgrading.`,
      warn: true,
    })
  }
  if (imp.seats_over > 0) {
    lines.push({
      text: `You're ${imp.seats_over} over the ${toName} seat limit; existing members keep access but you can't add more until you're under.`,
      warn: true,
    })
  }
  return lines
}

function DowngradeConfirm({
  fromTier,
  toTier,
  onCancel,
  onConfirm,
}: {
  fromTier: string
  toTier: string
  onCancel: () => void
  onConfirm: () => void
}) {
  const fromName = TIER_NAME[fromTier] ?? fromTier
  const toName = TIER_NAME[toTier] ?? toTier
  const [impact, setImpact] = useState<DowngradeImpact | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    fetch(`/api/v1/saas/billing/downgrade-impact?tier=${toTier}`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!cancelled) {
          setImpact(data)
          setLoading(false)
        }
      })
      .catch(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [toTier])

  const lines = impact ? impactLines(impact, toName) : []

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm">
      <div className="mx-4 w-full max-w-md rounded-2xl border border-border bg-card p-6 shadow-2xl">
        <h2 className="text-lg font-semibold">Downgrade to {toName}?</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          You're moving from {fromName} to {toName}. This takes effect immediately and Polar
          credits the unused portion of your current plan. Here's what changes:
        </p>
        {loading ? (
          <div className="mt-4 flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Checking what's affected…
          </div>
        ) : lines.length === 0 ? (
          <p className="mt-4 text-sm text-muted-foreground">
            Nothing you've configured is affected by this change.
          </p>
        ) : (
          <ul className="mt-4 space-y-2 text-sm">
            {lines.map((line) => (
              <li key={line.text} className="flex items-start gap-2">
                <span className={cn(line.warn ? "text-amber-500" : "text-rose-500")}>·</span>
                <span>{line.text}</span>
              </li>
            ))}
          </ul>
        )}
        <div className="mt-6 flex items-center justify-end gap-2">
          <Button variant="ghost" onClick={onCancel}>
            Keep {fromName}
          </Button>
          <Button onClick={onConfirm} className="bg-rose-600 text-white hover:bg-rose-500">
            Downgrade to {toName}
          </Button>
        </div>
      </div>
    </div>
  )
}

function BillingToggle({
  interval,
  onChange,
}: {
  interval: Interval
  onChange: (i: Interval) => void
}) {
  return (
    <div className="inline-flex items-center rounded-full border border-border bg-muted p-1">
      <button
        type="button"
        onClick={() => onChange("monthly")}
        className={cn(
          "rounded-full px-5 py-2 text-sm font-medium transition-all",
          interval === "monthly"
            ? "bg-rose-600 text-white shadow-sm"
            : "text-muted-foreground hover:text-foreground",
        )}
      >
        Monthly
      </button>
      <button
        type="button"
        onClick={() => onChange("annual")}
        className={cn(
          "flex items-center gap-2 rounded-full px-5 py-2 text-sm font-medium transition-all",
          interval === "annual"
            ? "bg-rose-600 text-white shadow-sm"
            : "text-muted-foreground hover:text-foreground",
        )}
      >
        Annual
        <span
          className={cn(
            "rounded-full px-2 py-0.5 text-2xs font-semibold uppercase tracking-wider",
            interval === "annual"
              ? "bg-white/25 text-white"
              : "bg-emerald-500/15 text-emerald-500",
          )}
        >
          Save 2 months
        </span>
      </button>
    </div>
  )
}

function PlanCard({
  plan,
  interval,
  index,
  loading,
  currentTier,
  currentInterval,
  billingStatus,
  onCheckout,
}: {
  plan: Plan
  interval: Interval
  index: number
  loading: boolean
  currentTier?: string
  currentInterval?: "monthly" | "annual"
  billingStatus?: string
  onCheckout: (tier: Tier) => void
}) {
  const isCustom = !!plan.customPrice
  const hasPlan =
    billingStatus === "active" ||
    billingStatus === "past_due" ||
    billingStatus === "cancelled"
  const isPendingCancel = billingStatus === "cancelled"
  const isOwnedTier = !!plan.tier && hasPlan && currentTier === plan.tier
  const sameInterval = currentInterval ? currentInterval === interval : true
  const isCurrentPlan = isOwnedTier && sameInterval && !isPendingCancel
  const intervalLabel = interval === "annual" ? "Annual" : "Monthly"
  const ownedIntervalLabel = currentInterval === "annual" ? "Annual" : "Monthly"
  const ctaLabel = isCurrentPlan
    ? "Current plan"
    : isPendingCancel && isOwnedTier && sameInterval
      ? `Reactivate ${plan.name} ${intervalLabel}`
      : hasPlan
        ? `Switch to ${plan.name} ${intervalLabel}`
        : `Upgrade to ${plan.name} ${intervalLabel}`

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: 0.05 + index * 0.05 }}
      className={cn(
        "relative flex flex-col rounded-2xl border p-6",
        plan.highlight
          ? "border-rose-500/40 bg-linear-to-b from-rose-500/8 via-transparent to-transparent shadow-[0_0_60px_-15px_rgba(244,63,94,0.35)]"
          : "border-border bg-card/40",
      )}
    >
      {plan.highlight && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full border border-rose-500/40 bg-background px-3 py-1 text-xs font-semibold uppercase tracking-wider text-rose-500">
          Most popular
        </div>
      )}

      <div className="mb-6">
        <div className="mb-2 flex items-center justify-between gap-2">
          <h2 className={cn("text-2xl font-bold tracking-tight", plan.highlight && "text-rose-500")}>
            {plan.name}
          </h2>
          {isOwnedTier && currentInterval && (
            <span
              className={cn(
                "inline-flex shrink-0 items-center gap-1 rounded-full border px-2 py-0.5 text-2xs font-semibold uppercase tracking-wider",
                isPendingCancel
                  ? "border-amber-500/40 bg-amber-500/8 text-amber-500"
                  : "border-emerald-500/40 bg-emerald-500/8 text-emerald-500",
              )}
            >
              <Check className="h-3 w-3" />
              {isPendingCancel
                ? `Your plan · ${ownedIntervalLabel} · ending`
                : `Your plan · ${ownedIntervalLabel}`}
            </span>
          )}
        </div>
        <p className="text-sm leading-relaxed text-muted-foreground">{plan.tagline}</p>
      </div>

      <div className="mb-6 min-h-22">
        {isCustom ? (
          <>
            <span className="text-3xl font-bold tracking-tight text-muted-foreground">
              {plan.customPrice}
            </span>
            {plan.customCaption && (
              <p className="mt-1.5 text-xs text-muted-foreground/70">{plan.customCaption}</p>
            )}
            {plan.customCaption2 && (
              <p className="mt-1 text-xs text-muted-foreground/70">{plan.customCaption2}</p>
            )}
          </>
        ) : (
          <>
            <AnimatePresence mode="wait">
              <motion.div
                key={interval}
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.15 }}
                className="flex items-baseline gap-1"
              >
                <span className="text-4xl font-bold tracking-tight tabular-nums">
                  {interval === "monthly" ? plan.monthlyPrice : plan.annualPrice}
                </span>
                <span className="text-base text-muted-foreground">/mo</span>
              </motion.div>
            </AnimatePresence>
            <p className="mt-1.5 text-xs text-muted-foreground/70">
              billed {interval === "monthly" ? "monthly" : "yearly"}
            </p>
          </>
        )}
      </div>

      {plan.enterprise ? (
        <a
          href="https://hivehook.com/enterprise"
          target="_blank"
          rel="noopener noreferrer"
          className="mb-7 w-full rounded-lg border border-border px-4 py-2.5 text-center text-sm font-semibold text-foreground transition-colors hover:bg-muted"
        >
          Talk to us
        </a>
      ) : (
        <button
          type="button"
          onClick={() => plan.tier && onCheckout(plan.tier)}
          disabled={loading || isCurrentPlan}
          className={cn(
            "mb-7 inline-flex w-full items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold transition-colors disabled:cursor-not-allowed",
            isCurrentPlan
              ? "border border-border bg-muted/40 text-muted-foreground"
              : plan.highlight
                ? "bg-rose-600 text-white hover:bg-rose-600/90 disabled:bg-rose-600/50"
                : "border border-border text-foreground hover:bg-muted disabled:opacity-60",
          )}
        >
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Redirecting
            </>
          ) : isCurrentPlan ? (
            <>
              <Check className="h-3.5 w-3.5" />
              {ctaLabel}
            </>
          ) : (
            <>
              {ctaLabel}
              <ArrowRight className="h-3.5 w-3.5" />
            </>
          )}
        </button>
      )}

      <ul className="space-y-2.5 text-sm">
        {plan.features.map((f) => {
          const isHeader = f.endsWith("plus:")
          return (
            <li
              key={f}
              className={cn("flex items-start gap-2.5", isHeader && "mt-3 border-t border-border/40 pt-3")}
            >
              {!isHeader && (
                <Check
                  className={cn(
                    "mt-0.5 h-4 w-4 shrink-0",
                    plan.highlight ? "text-rose-500" : "text-emerald-500",
                  )}
                />
              )}
              <span
                className={cn(
                  isHeader
                    ? "text-xs font-semibold uppercase tracking-wider text-muted-foreground"
                    : "text-foreground/90",
                )}
              >
                {f}
              </span>
            </li>
          )
        })}
      </ul>
    </motion.div>
  )
}
